import React, { memo, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Backdrop,
  CircularProgress,
  StepConnector,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
} from '@mui/material';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { styled } from '@mui/system';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';

import FYStepOne from '../../components/NewFYCreation/FYStepOne';
import FYSterpThree from '../../components/NewFYCreation/FYSterpThree';
import FYStepTwo from '../../components/NewFYCreation/FYStepTwo';
import FYStepFour from '../../components/NewFYCreation/FYStepFour';

import useSaveShortcut from '../../hooks/useSaveShortcut';
import { listFinancialYear } from '../../graphql';
import { useListCompanyPreferenceQuery } from '../../redux/apis/companyPreference.api';
import { setNavigationBlocked } from '../../redux/reducers/controllNavigation';
import { conditionalResetState, genericUpdate, setFYData } from '../../redux/reducers/FYSetupStep';
import { updateTitle } from '../../redux/reducers/titleSlice';
import { settingsClose } from '../../redux/reducers/companySkipSave';
import { showWarningSnackbar } from '../../components/snackBar/snakbar';

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  '&.Mui-completed .MuiStepConnector-line, &.Mui-active .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiStepConnector-line': {
    borderColor: '#a5a5ad',
  },
}));

function CreateNewFinancialYear() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [preferenceChecked, setPreferenceChecked] = useState(false);

  const { activeStep: reduxActiveStep, backdropOpen } = useSelector((state) => state.newFyStepsReducer, shallowEqual);
  const currentFY = useSelector((state) => state.userPrefrenceandReducer.financialYear, shallowEqual);
  const activeCompanyName = useSelector((state) => state.companyReducer.companyDetails.companyname);
  const saveSkipStatus = useSelector((state) => state.companySipSaveReducer.saveSkipStatus);

  const { data: companyPreference } = useListCompanyPreferenceQuery();

  const steps = useMemo(
    () => [
      { label: 'Financial Year' },
      { label: 'GST Registration Details' },
      { label: 'Transfer Balances' },
      { label: 'Voucher Number Formats' },
    ],
    []
  );

  useEffect(() => {
    if (!companyPreference) return;

    (async () => {
      const pendingPref = companyPreference.find(
        (pref) =>
          pref.attributetypecd.startsWith('NewFY Creation') &&
          parseInt(pref.additionalvalue.split('-')[0], 10) === currentFY.financialyearid
      );

      const [fyIdStr, stepNumberStr] = pendingPref.additionalvalue.split('-');
      const fyId = parseInt(fyIdStr, 10);
      const stepNumber = parseInt(stepNumberStr, 10);

      const listRes = await API.graphql(graphqlOperation(listFinancialYear));
      const allFY = listRes?.data?.listFinancialYear || [];

      let currentFyData = null;
      let newFyData = null;

      for (const fy of allFY) {
        if (fy.financialyearid === fyId) {
          currentFyData = fy;
        }
        if (fy.financialyearid === pendingPref.attributevalue) {
          newFyData = fy;
          if (!preferenceChecked) {
            dispatch(setFYData({ key: 'activeStep', data: stepNumber }));
            dispatch(setNavigationBlocked(true));
            dispatch(genericUpdate({ key: 'isCompleted', value: false }));
            setPreferenceChecked(true);
          }
        }
      }

      if (newFyData) {
        dispatch(setFYData({ key: 'newFYDta', data: newFyData }));
      }
      if (currentFyData) {
        dispatch(setFYData({ key: 'currentFYDta', data: currentFyData }));
      }
    })();
  }, [companyPreference, currentFY.financialyearid, dispatch, preferenceChecked]);

  useEffect(() => {
    if (saveSkipStatus === false) {
      dispatch(settingsClose());
      navigate('/companysetup');
      return;
    }
    if (activeCompanyName) {
      dispatch(updateTitle(`Financial Year Creation for ${activeCompanyName}`));
    }
  }, [activeCompanyName, saveSkipStatus, dispatch, navigate]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  useSaveShortcut(() => {
    if (saveSkipStatus === false && window.location.href.includes('companysetup')) {
      showWarningSnackbar('Please Complete the Company Setup Details');
      dispatch(settingsClose());
      navigate('/companysetup');
    }
  });

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 9, lg: 5 },
        pb: 1,
        boxSizing: 'content-box',
      }}
    >
      <Paper sx={{ borderRadius: 2, p: { xs: 2, lg: 3 } }}>
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0)',
          }}
          open={backdropOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={reduxActiveStep} alternativeLabel connector={<CustomConnector />}>
            {steps.map(({ label }) => (
              <Step key={label}>
                <StepLabel sx={{ fontSize: '17px' }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {reduxActiveStep === 0 && <FYStepOne locationState={locationState} />}
        {reduxActiveStep === 1 && <FYStepFour />}
        {reduxActiveStep === 2 && <FYSterpThree />}
        {reduxActiveStep === 3 && <FYStepTwo />}

        <Dialog open={dialogOpen} onClose={handleDialogClose}>
          <DialogTitle
            sx={{
              backgroundColor: 'primary.main',
              color: '#fff',
              px: 1,
              py: 1,
            }}
          >
            Incomplete Steps
          </DialogTitle>
          <DialogContent sx={{ p: 0, px: 1, maxWidth: 500 }}>
            <DialogContentText>You have not completed all steps. Please complete all steps before navigating away.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleDialogClose}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}

export default memo(CreateNewFinancialYear);
